import { z } from 'zod';

/** Backend rule for staff passwords. */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 200;

const email = z.email({ error: 'auth:validation.email' });
const name = z
  .string()
  .trim()
  .min(1, { error: 'auth:validation.required' })
  .max(120, { error: 'auth:validation.nameLength' });
const password = z
  .string()
  .min(PASSWORD_MIN, { error: 'auth:validation.passwordLength' })
  .max(PASSWORD_MAX, { error: 'auth:validation.passwordLength' });

export const forgotPasswordSchema = z.object({ email });
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({ password, confirmPassword: z.string() })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    error: 'auth:validation.passwordMismatch',
  });
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

/** `requiresPassword` mirrors the invitation preview: an invitee who already has an account sets none. */
export const acceptInviteSchema = z
  .object({
    name,
    requiresPassword: z.boolean(),
    password: z.string(),
    confirmPassword: z.string(),
  })
  .superRefine((v, ctx) => {
    if (!v.requiresPassword) return;
    if (v.password.length < PASSWORD_MIN || v.password.length > PASSWORD_MAX) {
      ctx.addIssue({
        code: 'custom',
        path: ['password'],
        message: 'auth:validation.passwordLength',
      });
    } else if (v.password !== v.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'auth:validation.passwordMismatch',
      });
    }
  });
export type AcceptInviteInput = z.input<typeof acceptInviteSchema>;
export type AcceptInviteOutput = z.output<typeof acceptInviteSchema>;
